/**
 * markdownTableParser.ts
 *
 * Converts Markdown tables to styled HTML tables.
 * Uses a placeholder system so that \n -> <br> conversions
 * in formatMessage don't break table structure.
 */

/** Split a markdown table row like "| A | B | C |" into ["A", "B", "C"] */
export const parseTableRow = (row: string): string[] => {
  return row.split('|').slice(1, -1).map(cell => cell.trim());
};

/** Apply inline formatting (bold, italic) to table cell content */
export const formatCellContent = (cell: string): string => {
  return cell
    .replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold">$1</strong>')
    .replace(/\*(.*?)\*/g, '<em class="italic">$1</em>');
};

/** Determine text-alignment Tailwind class from separator cell */
export const getAlignClass = (separator: string): string => {
  const s = separator.trim();
  if (s.startsWith(':') && s.endsWith(':')) return 'text-center';
  if (s.endsWith(':')) return 'text-right';
  return 'text-left';
};

export interface TableExtractionResult {
  content: string;
  tables: Map<string, string>;
}

/**
 * Extract markdown tables from content and replace them with placeholders.
 */
export const convertMarkdownTables = (content: string): TableExtractionResult => {
  const tables = new Map<string, string>();
  const lines = content.split('\n');
  const result: string[] = [];
  let i = 0;
  let tableIndex = 0;

  while (i < lines.length) {
    const line = lines[i].trim();

    if (line.startsWith('|') && line.endsWith('|')) {
      const tableLines: string[] = [];
      while (i < lines.length && lines[i].trim().startsWith('|') && lines[i].trim().endsWith('|')) {
        tableLines.push(lines[i].trim());
        i++;
      }

      const hasSeparator = tableLines.length >= 2 && /^[\s|:\-]+$/.test(tableLines[1]);

      if (hasSeparator) {
        const placeholder = `__MDTABLE_${tableIndex}__`;
        tableIndex++;

        const headerCells = parseTableRow(tableLines[0]);
        const separatorCells = parseTableRow(tableLines[1]);
        const alignments = separatorCells.map(getAlignClass);

        let html = '<div class="overflow-x-auto my-4 rounded-lg border border-gray-200 dark:border-gray-600">';
        html += '<table class="min-w-full border-collapse text-sm">';

        html += '<thead><tr class="bg-gray-100 dark:bg-gray-700">';
        headerCells.forEach((cell, idx) => {
          const align = alignments[idx] || 'text-left';
          html += `<th class="border-b border-gray-300 dark:border-gray-600 px-4 py-2.5 ${align} font-semibold text-gray-900 dark:text-gray-100">${formatCellContent(cell)}</th>`;
        });
        html += '</tr></thead>';

        html += '<tbody>';
        for (let r = 2; r < tableLines.length; r++) {
          const dataCells = parseTableRow(tableLines[r]);
          const stripe = r % 2 === 0
            ? 'bg-white dark:bg-gray-800'
            : 'bg-gray-50 dark:bg-gray-800/60';
          html += `<tr class="${stripe} hover:bg-gray-100/60 dark:hover:bg-gray-700/40 transition-colors">`;
          dataCells.forEach((cell, idx) => {
            const align = alignments[idx] || 'text-left';
            html += `<td class="border-b border-gray-200 dark:border-gray-700 px-4 py-2 ${align} text-gray-700 dark:text-gray-300">${formatCellContent(cell)}</td>`;
          });
          html += '</tr>';
        }
        html += '</tbody></table></div>';

        tables.set(placeholder, html);
        result.push(placeholder);
      } else {
        tableLines.forEach(tl => result.push(tl));
      }
    } else {
      result.push(lines[i]);
      i++;
    }
  }

  return { content: result.join('\n'), tables };
};

/**
 * After \n -> <br> conversion, restore table HTML from placeholders.
 */
export const restoreTablePlaceholders = (formatted: string, tables: Map<string, string>): string => {
  let result = formatted;
  tables.forEach((tableHtml, placeholder) => {
    result = result.replace(
      new RegExp(`(?:<br>)*${placeholder.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?:<br>)*`, 'g'),
      tableHtml
    );
  });
  return result;
};
